import React, { useState, useEffect } from 'react';
import { api } from '../App';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { Plus, Save, Trash2 } from 'lucide-react';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';

const JobTemplatesTab = () => {
  const [templates, setTemplates] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    job_type: 'apartment',
    unit_type: '',
    approx_sqft: '',
    default_unit_count: 1,
    base_labour_hours_min: '',
    base_labour_hours_max: '',
    default_team_size: 3,
    default_internal_rate_per_hour: 20,
    default_supplies_cost: '',
    default_target_margin_pct: 40,
    notes_internal: ''
  });

  useEffect(() => {
    fetchTemplates();
  }, []);

  const fetchTemplates = async () => {
    try {
      setLoading(true);
      const response = await api.get('/estimate-templates');
      setTemplates(response.data || []);
    } catch (error) {
      console.error('Error fetching templates:', error);
      toast.error('Failed to load templates');
    } finally {
      setLoading(false);
    }
  };

  const handleSelectTemplate = (template) => {
    setSelectedTemplate(template);
    setFormData({
      name: template.name,
      job_type: template.job_type,
      unit_type: template.unit_type,
      approx_sqft: template.approx_sqft || '',
      default_unit_count: template.default_unit_count || 1,
      base_labour_hours_min: template.base_labour_hours_min,
      base_labour_hours_max: template.base_labour_hours_max,
      default_team_size: template.default_team_size,
      default_internal_rate_per_hour: template.default_internal_rate_per_hour,
      default_supplies_cost: template.default_supplies_cost || '',
      default_target_margin_pct: template.default_target_margin_pct || 40,
      notes_internal: template.notes_internal || ''
    });
  };

  const handleNewTemplate = () => {
    setSelectedTemplate(null);
    setFormData({
      name: '',
      job_type: 'apartment',
      unit_type: '',
      approx_sqft: '',
      default_unit_count: 1,
      base_labour_hours_min: '',
      base_labour_hours_max: '',
      default_team_size: 3,
      default_internal_rate_per_hour: 20,
      default_supplies_cost: '',
      default_target_margin_pct: 40,
      notes_internal: ''
    });
  };

  const handleSave = async () => {
    if (!formData.name || !formData.unit_type || !formData.base_labour_hours_min || !formData.base_labour_hours_max) {
      toast.error('Please fill in all required fields');
      return;
    }

    try {
      setLoading(true);
      const templateData = {
        ...formData,
        approx_sqft: formData.approx_sqft ? parseInt(formData.approx_sqft) : null,
        base_labour_hours_min: parseFloat(formData.base_labour_hours_min),
        base_labour_hours_max: parseFloat(formData.base_labour_hours_max),
        default_unit_count: parseInt(formData.default_unit_count) || 1,
        default_team_size: parseInt(formData.default_team_size),
        default_internal_rate_per_hour: parseFloat(formData.default_internal_rate_per_hour),
        default_supplies_cost: formData.default_supplies_cost ? parseFloat(formData.default_supplies_cost) : null,
        default_target_margin_pct: formData.default_target_margin_pct ? parseFloat(formData.default_target_margin_pct) : null
      };

      if (selectedTemplate) {
        await api.put(`/estimate-templates/${selectedTemplate.id}`, templateData);
        toast.success('Template updated successfully');
      } else {
        await api.post('/estimate-templates', templateData);
        toast.success('Template created successfully');
      }

      await fetchTemplates();
      handleNewTemplate();
    } catch (error) {
      console.error('Error saving template:', error);
      toast.error('Failed to save template');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!selectedTemplate) return;
    
    if (!window.confirm('Are you sure you want to delete this template?')) {
      return;
    }

    try {
      await api.delete(`/estimate-templates/${selectedTemplate.id}`);
      toast.success('Template deleted successfully');
      await fetchTemplates();
      handleNewTemplate();
    } catch (error) {
      console.error('Error deleting template:', error);
      toast.error('Failed to delete template');
    }
  };

  return (
    <div className="grid grid-cols-2 gap-6 h-full">
      {/* Left Pane - Templates List */}
      <Card className="flex flex-col">
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle>Job Templates</CardTitle>
          <Button
            onClick={handleNewTemplate}
            className="bg-[#247C75] hover:bg-[#1d6259]"
            size="sm"
          >
            <Plus className="h-4 w-4 mr-2" />
            New Template
          </Button>
        </CardHeader>
        <CardContent className="flex-1 overflow-auto">
          {loading && templates.length === 0 ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#247C75]"></div>
            </div>
          ) : templates.length === 0 ? (
            <div className="text-center py-12">
              <p className="text-slate-600">No templates found</p>
              <Button
                variant="outline"
                onClick={handleNewTemplate}
                className="mt-4"
              >
                Create your first template
              </Button>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Template Name</TableHead>
                  <TableHead>Job Type</TableHead>
                  <TableHead>Unit Type</TableHead>
                  <TableHead>Sq Ft</TableHead>
                  <TableHead>Team Size</TableHead>
                  <TableHead>Updated</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {templates.map((template) => (
                  <TableRow
                    key={template.id}
                    onClick={() => handleSelectTemplate(template)}
                    className={`cursor-pointer hover:bg-slate-50 ${
                      selectedTemplate?.id === template.id ? 'bg-blue-50' : ''
                    }`}
                  >
                    <TableCell className="font-medium">{template.name}</TableCell>
                    <TableCell className="capitalize">{template.job_type}</TableCell>
                    <TableCell>{template.unit_type}</TableCell>
                    <TableCell>{template.approx_sqft || '-'}</TableCell>
                    <TableCell>{template.default_team_size}</TableCell>
                    <TableCell>
                      {template.updated_at ? new Date(template.updated_at).toLocaleDateString() : '-'}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Right Pane - Template Form */}
      <Card className="flex flex-col">
        <CardHeader>
          <CardTitle>{selectedTemplate ? 'Edit Template' : 'New Template'}</CardTitle>
        </CardHeader>
        <CardContent className="flex-1 overflow-auto space-y-4">
          {/* Template Name */}
          <div className="space-y-2">
            <Label htmlFor="name">Template Name *</Label>
            <Input
              id="name"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              placeholder="e.g., Standard Apartment - 2BR/2BA"
            />
          </div>

          {/* Job Type & Unit Type */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="job_type">Job Type *</Label>
              <Select
                value={formData.job_type}
                onValueChange={(value) => setFormData({ ...formData, job_type: value })}
              >
                <SelectTrigger id="job_type">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="apartment">Apartment</SelectItem>
                  <SelectItem value="townhome">Townhome</SelectItem>
                  <SelectItem value="commercial">Commercial</SelectItem>
                  <SelectItem value="common_area">Common Area</SelectItem>
                  <SelectItem value="other">Other</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="unit_type">Unit Type *</Label>
              <Input
                id="unit_type"
                value={formData.unit_type}
                onChange={(e) => setFormData({ ...formData, unit_type: e.target.value })}
                placeholder="e.g., 2B/2B"
              />
            </div>
          </div>

          {/* Approx Sq Ft & Default Unit Count */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="approx_sqft">Approx Sq Ft</Label>
              <Input
                id="approx_sqft"
                type="number"
                value={formData.approx_sqft}
                onChange={(e) => setFormData({ ...formData, approx_sqft: e.target.value })}
                placeholder="e.g., 900"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="default_unit_count">Default Unit Count</Label>
              <Input
                id="default_unit_count"
                type="number"
                min="1"
                value={formData.default_unit_count}
                onChange={(e) => setFormData({ ...formData, default_unit_count: e.target.value })}
              />
            </div>
          </div>

          {/* Base Labour Hours */}
          <div className="space-y-2">
            <Label>Base Labour Hours (per unit) *</Label>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="hours_min" className="text-xs text-slate-600">Min</Label>
                <Input
                  id="hours_min"
                  type="number"
                  step="0.1"
                  value={formData.base_labour_hours_min}
                  onChange={(e) => setFormData({ ...formData, base_labour_hours_min: e.target.value })}
                  placeholder="e.g., 7"
                />
              </div>
              <div>
                <Label htmlFor="hours_max" className="text-xs text-slate-600">Max</Label>
                <Input
                  id="hours_max"
                  type="number"
                  step="0.1"
                  value={formData.base_labour_hours_max}
                  onChange={(e) => setFormData({ ...formData, base_labour_hours_max: e.target.value })}
                  placeholder="e.g., 9"
                />
              </div>
            </div>
          </div>

          {/* Default Team Size */}
          <div className="space-y-2">
            <Label htmlFor="team_size">Default Team Size *</Label>
            <Select
              value={formData.default_team_size.toString()}
              onValueChange={(value) => setFormData({ ...formData, default_team_size: parseInt(value) })}
            >
              <SelectTrigger id="team_size">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="2">2</SelectItem>
                <SelectItem value="3">3</SelectItem>
                <SelectItem value="4">4</SelectItem>
                <SelectItem value="5">5</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Default Internal Rate */}
          <div className="space-y-2">
            <Label htmlFor="hourly_rate">Default Internal Rate per Hour ($) *</Label>
            <Input
              id="hourly_rate"
              type="number"
              step="0.01"
              value={formData.default_internal_rate_per_hour}
              onChange={(e) => setFormData({ ...formData, default_internal_rate_per_hour: e.target.value })}
              placeholder="e.g., 20"
            />
          </div>

          {/* Default Supplies Cost */}
          <div className="space-y-2">
            <Label htmlFor="supplies_cost">Default Supplies Cost ($)</Label>
            <Input
              id="supplies_cost"
              type="number"
              step="0.01"
              value={formData.default_supplies_cost}
              onChange={(e) => setFormData({ ...formData, default_supplies_cost: e.target.value })}
              placeholder="e.g., 50"
            />
          </div>

          {/* Default Target Margin */}
          <div className="space-y-2">
            <Label htmlFor="margin_pct">Default Target Margin (%)</Label>
            <Input
              id="margin_pct"
              type="number"
              step="1"
              value={formData.default_target_margin_pct}
              onChange={(e) => setFormData({ ...formData, default_target_margin_pct: e.target.value })}
              placeholder="e.g., 40"
            />
          </div>

          {/* Internal Notes */}
          <div className="space-y-2">
            <Label htmlFor="notes">Internal Notes</Label>
            <textarea
              id="notes"
              value={formData.notes_internal}
              onChange={(e) => setFormData({ ...formData, notes_internal: e.target.value })}
              className="w-full min-h-[100px] rounded-md border border-slate-300 p-3 text-sm focus:ring-2 focus:ring-[#247C75] focus:border-transparent"
              placeholder="Additional guidelines or special considerations"
            />
          </div>

          {/* Action Buttons */}
          <div className="flex gap-3 pt-4 border-t">
            <Button
              onClick={handleSave}
              disabled={loading}
              className="flex-1 bg-[#247C75] hover:bg-[#1d6259]"
            >
              <Save className="h-4 w-4 mr-2" />
              Save Template
            </Button>
            {selectedTemplate && (
              <Button
                onClick={handleDelete}
                variant="outline"
                className="border-red-200 text-red-600 hover:bg-red-50"
              >
                <Trash2 className="h-4 w-4 mr-2" />
                Delete
              </Button>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default JobTemplatesTab;
